import React, { useCallback, useEffect, useRef, useState } from 'react';

import { apiPatch } from '../../../lib/api_v2';
import { logError } from '../../../lib/logger';

import { KEYCHAIN } from '../../../contexts/KeyMaster';
import { responseValidations } from '../account/accountStyles';
import { SETTINGS_MODAL_SAVE_PILL_CLASS } from '../../shared/settingsModalChrome';
import FlexiconEditor from '../../FlexiconEditor';

// NOTE: this panel renders inside the workspace-options modal, so it must NOT push its own
// KeyMaster entry — that sits above the modal's keychain and blocks escape/outside-press.
const GeneralSettings = ({ workspace, onSaved }) => {
  const workspaceId = workspace?.id;
  const workspaceTitle = workspace?.title || '';
  const [errors, setErrors] = useState({});
  const [isFlexiconUploading, setIsFlexiconUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [title, setTitle] = useState('');
  // tracks the effective flexicon kind without waiting for the workspace prop to propagate —
  // updated immediately when FlexiconEditor applies a change, and synced when workspace updates
  const liveFlexiconRef = useRef({ kind: null, glyphKind: null });

  useEffect(() => {
    if (workspaceId) setTitle(workspaceTitle);
  }, [workspaceId, workspaceTitle]);

  useEffect(() => {
    liveFlexiconRef.current = { kind: workspace?.flexicon?.kind, glyphKind: workspace?.flexicon?.glyph?.kind };
  }, [workspace?.flexicon?.kind, workspace?.flexicon?.glyph?.kind]);

  const handleTitleChange = useCallback((e) => setTitle(e.target.value), []);

  const handleFlexiconChange = useCallback(({ flexicon_type }) => {
    liveFlexiconRef.current = { kind: flexicon_type, glyphKind: flexicon_type === 'glyph' ? 'letters' : null };
  }, []);

  const handleUploadingChange = useCallback((uploading) => {
    setIsFlexiconUploading(uploading);
  }, []);

  const handleSave = useCallback(
    async (e) => {
      e?.preventDefault();
      if (isFlexiconUploading || !workspaceId) return;

      setIsSaving(true);
      setErrors({});

      try {
        const workspaceData = { title };

        // if workspace uses a letter glyph (not a user-set emoji/image/other glyph), update it to match the new title.
        // read from liveFlexiconRef (not workspace prop) so a just-saved emoji/image isn't overwritten by stale prop data.
        const { kind: liveKind, glyphKind: liveGlyphKind } = liveFlexiconRef.current;
        if (!liveKind || (liveKind === 'glyph' && liveGlyphKind === 'letters')) {
          const glyphs = JSON.parse(sessionStorage.getItem('output:glyphs:letters') || '[]');
          const letter = title.toLowerCase().match(/[a-z]/)?.[0] || '_';
          const glyph = glyphs.find((g) => g.filename === letter) || glyphs.find((g) => g.filename === '_');
          if (glyph) {
            workspaceData.flexicon_type = 'glyph';
            workspaceData.flexicon_id = glyph.id;
          }
        }

        const response = await apiPatch(`/workspaces/${workspaceId}`, { workspace: workspaceData });
        const data = await response.json();
        if (data.ok) {
          // Popping back to the settings root doubles as the success feedback
          onSaved?.();
        } else {
          const validation_errors = responseValidations(data);
          setErrors(validation_errors || { general: 'Unable to save settings. Please try again.' });
        }
      } catch (error) {
        logError('GeneralSettings', 'Failed to save workspace:', error);
        setErrors({ general: 'Failed to save settings. Please try again.' });
      } finally {
        setIsSaving(false);
      }
    },
    [title, isFlexiconUploading, onSaved, workspaceId],
  );

  const titleErrors = Array.isArray(errors.title) ? errors.title.join(', ') : errors.title;
  // Save only governs the group name (avatar and wallpaper apply instantly), so it
  // stays dormant until the name actually changes.
  const isDirty = title.trim() !== workspaceTitle && title.trim().length > 0;

  return (
    <form onSubmit={handleSave} className="w-full pt-8">
      {/* Sits in the modal top bar, opposite the back button */}
      <button
        type="submit"
        data-app-region="none"
        disabled={isSaving || isFlexiconUploading || !workspaceId || !isDirty}
        className={SETTINGS_MODAL_SAVE_PILL_CLASS}
      >
        {isSaving ? '...' : 'Save'}
      </button>

      {errors.general && <div className="mb-5 rounded-md bg-red-50 p-4 text-sm text-red-800">{errors.general}</div>}

      <div className="flex w-full justify-center">
        <FlexiconEditor
          resource={workspace}
          resourceType="workspace"
          keychainId={KEYCHAIN.MODAL__WORKSPACE_OPTIONS}
          nameValue={title}
          layout="stacked"
          actionsPresentation="overlay"
          previewSize="size-30"
          onFlexiconChange={handleFlexiconChange}
          onUploadingChange={handleUploadingChange}
        />
      </div>

      <div className="mt-8">
        <label htmlFor="title" className="block text-cursed text-black dark:text-neutral-100">
          Group name
        </label>
        <div className="mt-2">
          <input
            type="text"
            id="title"
            name="title"
            value={title}
            onChange={handleTitleChange}
            required
            placeholder="Acme"
            autoComplete="off"
            data-1p-ignore="true"
            className="h-12 w-full rounded-lg border sm:border-[0.5px] border-white bg-gray-50 px-4 text-[15px] leading-5 text-black placeholder:text-gray-400 hover:bg-white focus:outline-none dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100"
          />
        </div>
        {titleErrors && <span className="mt-1 block text-left text-xs text-red-400">{titleErrors}</span>}
      </div>
    </form>
  );
};

export default GeneralSettings;
